/**
 * Voie d) « Saisie libre » (specs §6.3-d).
 *
 * Saisie directe des quatre macros pour un aliment ou un repas ponctuel, **sans
 * créer de fiche en base** — c'est la définition même de cette voie. Extraite de
 * `EntrySheet.tsx` à l'étape 6, quand les trois autres voies l'ont rejointe.
 *
 * ⚠️ Les quatre valeurs saisies **sont** l'entrée : `quantity` et `unit` restent
 * descriptifs, jamais des multiplicateurs. Le serveur normalise (arrondi,
 * refus des négatifs) et écrit ce qu'il a produit (§5.1).
 */

import { useState } from 'react';

import type { LogEntryInput } from '../../../../shared/types';
import { Button, Field, Hint, NumberField } from '../../components/ui/primitives';
import { macrosOf, type MacroFields } from '../../lib/formFields';
import { MACRO_ROWS } from '../../lib/macros';
import type { EntryPreview } from '../../hooks/useDay';

export function FreeEntryForm({
  initialLabel = '',
  initialMacros,
  submitLabel,
  note,
  onSubmit,
}: {
  initialLabel?: string;
  initialMacros?: MacroFields;
  submitLabel: string;
  note?: string;
  onSubmit: (input: LogEntryInput, preview: EntryPreview) => void;
}) {
  const [label, setLabel] = useState(initialLabel);
  const [fields, setFields] = useState<MacroFields>(
    () => initialMacros ?? { protein: '', carbs: '', fat: '', kcal: '' },
  );

  const macros = macrosOf(fields);
  const canSubmit = label.trim() !== '';

  const submit = () => {
    if (!canSubmit) return;
    const trimmed = label.trim();
    onSubmit(
      { type: 'quick', label: trimmed, macros },
      { label: trimmed, quantity: 1, unit: 'portion', portionLabel: null, macros },
    );
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
    >
      <Field
        label="Aliment ou repas"
        value={label}
        onChange={setLabel}
        placeholder="Poulet basquaise"
        autoFocus
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: '0.5rem',
        }}
      >
        {MACRO_ROWS.map((row) => (
          <NumberField
            key={row.key}
            label={row.short}
            suffix={row.key === 'kcal' ? undefined : 'g'}
            value={fields[row.key]}
            onChange={(value) => {
              setFields((current) => ({ ...current, [row.key]: value }));
            }}
          />
        ))}
      </div>

      {/*
        Aide de saisie, pas une correction. Les quatre valeurs restent
        indépendantes (§4.4) et le serveur n'en dérive aucune : ce bouton se
        contente de remplir un champ à la place de l'utilisateur, qui peut
        ensuite écrire ce qu'il veut. À distinguer de l'écart kcal du bandeau,
        qui lui ne propose jamais rien (specs §6.2).
      */}
      <button
        type="button"
        onClick={() => {
          const theoretical = macros.protein * 4 + macros.carbs * 4 + macros.fat * 9;
          setFields((current) => ({
            ...current,
            kcal: String(Math.round(theoretical * 100) / 100),
          }));
        }}
        className="n-btn n-btn--ghost n-btn--rounded"
        style={{ alignSelf: 'flex-start' }}
      >
        Remplir les kcal en 4/4/9
      </button>

      <Button type="submit" disabled={!canSubmit}>
        {submitLabel}
      </Button>

      <Hint>
        {note ??
          'Saisie ponctuelle : aucune fiche n’est créée en base. Les macros sont figées à l’enregistrement et ne bougeront plus jamais toutes seules.'}
      </Hint>
    </form>
  );
}
